import React, { useEffect, useState } from 'react';
import {
  Container,
  Table,
  Loader,
  Segment,
  Header,
  Label,
  Button,
  Breadcrumb,
  Icon,
  Dropdown,
  Modal,
  Form,
  TextArea,
  Input,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { fetchApi, getApiPath } from '../../api';

const priorityColors = {
  'in-progress': 'blue',
  next: 'orange',
  planned: 'teal',
  wish: 'grey',
  done: 'green',
  wontdo: 'black',
};

const priorityIcons = {
  'in-progress': 'spinner',
  next: 'arrow right',
  planned: 'calendar outline',
  wish: 'star outline',
  done: 'check circle',
  wontdo: 'ban',
};

const categoryIcons = {
  ui: 'paint brush',
  infrastructure: 'server',
  compliance: 'shield',
  translation: 'language',
  integration: 'plug',
  security: 'lock',
  general: 'tag',
};

const FeatureBacklog = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newFeature, setNewFeature] = useState({
    title: '',
    description: '',
    category: 'general',
    priority: 'wish',
    notes: '',
  });
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (filterStatus) params.append('status', filterStatus);
      if (showAll) params.append('show_all', '1');
      const qs = params.toString();
      const data = await fetchApi(`${getApiPath()}/@polyglot/features${qs ? '?' + qs : ''}`);
      setFeatures(data.items || []);
      setCategories(data.categories || []);
      setPriorities(data.priorities || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
  }, [filterCategory, filterStatus, showAll]);

  const updatePriority = async (id, priority) => {
    try {
      await fetchApi(`${getApiPath()}/@polyglot/features/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priority }),
      });
      fetchFeatures();
    } catch (err) {
      setError(err.message);
    }
  };

  const addFeature = async () => {
    if (!newFeature.title.trim()) return;
    try {
      await fetchApi(`${getApiPath()}/@polyglot/features`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newFeature),
      });
      setShowAddModal(false);
      setNewFeature({
        title: '',
        description: '',
        category: 'general',
        priority: 'wish',
        notes: '',
      });
      fetchFeatures();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Loader active inline="centered">
          Loading feature backlog...
        </Loader>
      </Container>
    );
  }

  const categoryOptions = [
    { key: '', value: '', text: 'All categories' },
    ...categories.map((c) => ({
      key: c,
      value: c,
      text: c.charAt(0).toUpperCase() + c.slice(1),
      icon: categoryIcons[c] || 'tag',
    })),
  ];

  const priorityOptions = [
    { key: '', value: '', text: 'Active (hide done)' },
    ...priorities.map((p) => ({
      key: p,
      value: p,
      text: p.charAt(0).toUpperCase() + p.slice(1),
    })),
  ];

  const counts = {};
  features.forEach((f) => {
    counts[f.priority] = (counts[f.priority] || 0) + 1;
  });

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '4em' }}>
      <Helmet title="Feature Backlog" />

      <Breadcrumb style={{ marginBottom: '1em' }}>
        <Breadcrumb.Section href="/polyglot">Monster Machine</Breadcrumb.Section>
        <Breadcrumb.Divider />
        <Breadcrumb.Section active>Feature Backlog</Breadcrumb.Section>
      </Breadcrumb>

      <Header as="h1" style={{ marginBottom: '0.5em' }}>
        <Icon name="lightbulb outline" />
        <Header.Content>
          Feature Backlog
          <Header.Subheader>
            Ideas, wishes, and planned work — {features.length} items
          </Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Segment color="red" inverted>
          {error}
        </Segment>
      )}

      <Segment>
        <div
          style={{
            display: 'flex',
            gap: '1em',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Dropdown
            selection
            placeholder="Category"
            options={categoryOptions}
            value={filterCategory}
            onChange={(e, { value }) => setFilterCategory(value)}
            style={{ minWidth: '180px' }}
          />
          <Dropdown
            selection
            placeholder="Status"
            options={priorityOptions}
            value={filterStatus}
            onChange={(e, { value }) => {
              setFilterStatus(value);
              if (value) setShowAll(true);
              else setShowAll(false);
            }}
            style={{ minWidth: '180px' }}
          />
          <Button
            primary
            icon="plus"
            content="Add Feature"
            onClick={() => setShowAddModal(true)}
          />
          <div style={{ marginLeft: 'auto' }}>
            {Object.entries(counts).map(([p, c]) => (
              <Label
                key={p}
                color={priorityColors[p]}
                style={{ marginBottom: '2px' }}
              >
                {p}
                <Label.Detail>{c}</Label.Detail>
              </Label>
            ))}
          </div>
        </div>
      </Segment>

      <Table celled selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={1}>#</Table.HeaderCell>
            <Table.HeaderCell width={1}>Priority</Table.HeaderCell>
            <Table.HeaderCell width={1}>Category</Table.HeaderCell>
            <Table.HeaderCell>Title</Table.HeaderCell>
            <Table.HeaderCell width={2}>DR</Table.HeaderCell>
            <Table.HeaderCell width={2}>Requested</Table.HeaderCell>
            <Table.HeaderCell width={2}>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {features.map((f) => (
            <React.Fragment key={f.id}>
              <Table.Row
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  setExpandedId(expandedId === f.id ? null : f.id)
                }
              >
                <Table.Cell style={{ color: '#999', textAlign: 'center' }}>
                  {f.id}
                </Table.Cell>
                <Table.Cell>
                  <Label
                    color={priorityColors[f.priority]}
                    size="small"
                    style={{ minWidth: '80px', textAlign: 'center' }}
                  >
                    <Icon name={priorityIcons[f.priority]} />
                    {f.priority}
                  </Label>
                </Table.Cell>
                <Table.Cell>
                  <Icon name={categoryIcons[f.category] || 'tag'} />
                  {f.category}
                </Table.Cell>
                <Table.Cell>
                  <strong>{f.title}</strong>
                </Table.Cell>
                <Table.Cell>
                  {f.target_dr && (
                    <Label size="small" color="blue">
                      {f.target_dr}
                    </Label>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {new Date(f.requested_at).toLocaleDateString()}
                </Table.Cell>
                <Table.Cell onClick={(e) => e.stopPropagation()}>
                  <Dropdown
                    icon="ellipsis horizontal"
                    floating
                    button
                    className="icon mini"
                    direction="left"
                  >
                    <Dropdown.Menu>
                      {priorities
                        .filter((p) => p !== f.priority)
                        .map((p) => (
                          <Dropdown.Item
                            key={p}
                            icon={priorityIcons[p]}
                            text={`Set ${p}`}
                            onClick={() => updatePriority(f.id, p)}
                          />
                        ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Table.Cell>
              </Table.Row>
              {expandedId === f.id && (
                <Table.Row>
                  <Table.Cell colSpan="7">
                    <Segment secondary>
                      {f.description && (
                        <p style={{ whiteSpace: 'pre-wrap' }}>
                          {f.description}
                        </p>
                      )}
                      {f.notes && (
                        <p>
                          <strong>Notes:</strong>{' '}
                          <span style={{ whiteSpace: 'pre-wrap' }}>
                            {f.notes}
                          </span>
                        </p>
                      )}
                      <p style={{ color: '#999', fontSize: '0.9em' }}>
                        Requested by {f.requested_by} on{' '}
                        {new Date(f.requested_at).toLocaleDateString()} | Last
                        updated {new Date(f.updated_at).toLocaleDateString()}
                        {f.completed_at &&
                          ` | Completed ${new Date(f.completed_at).toLocaleDateString()}`}
                      </p>
                    </Segment>
                  </Table.Cell>
                </Table.Row>
              )}
            </React.Fragment>
          ))}
          {features.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan="7" textAlign="center">
                No feature requests match the current filters.
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>

      <Modal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        size="small"
      >
        <Modal.Header>
          <Icon name="lightbulb outline" /> New Feature Request
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field required>
              <label>Title</label>
              <Input
                placeholder="Short descriptive title"
                value={newFeature.title}
                onChange={(e) =>
                  setNewFeature({ ...newFeature, title: e.target.value })
                }
              />
            </Form.Field>
            <Form.Field>
              <label>Description</label>
              <TextArea
                placeholder="What should this feature do? Why is it needed?"
                value={newFeature.description}
                onChange={(e) =>
                  setNewFeature({ ...newFeature, description: e.target.value })
                }
                rows={4}
              />
            </Form.Field>
            <Form.Group widths="equal">
              <Form.Field>
                <label>Category</label>
                <Dropdown
                  selection
                  options={categories.map((c) => ({
                    key: c,
                    value: c,
                    text: c.charAt(0).toUpperCase() + c.slice(1),
                  }))}
                  value={newFeature.category}
                  onChange={(e, { value }) =>
                    setNewFeature({ ...newFeature, category: value })
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>Priority</label>
                <Dropdown
                  selection
                  options={priorities.map((p) => ({
                    key: p,
                    value: p,
                    text: p.charAt(0).toUpperCase() + p.slice(1),
                  }))}
                  value={newFeature.priority}
                  onChange={(e, { value }) =>
                    setNewFeature({ ...newFeature, priority: value })
                  }
                />
              </Form.Field>
            </Form.Group>
            <Form.Field>
              <label>Notes</label>
              <TextArea
                placeholder="Additional context, references, constraints..."
                value={newFeature.notes}
                onChange={(e) =>
                  setNewFeature({ ...newFeature, notes: e.target.value })
                }
                rows={2}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button
            primary
            icon="plus"
            content="Add Feature"
            onClick={addFeature}
            disabled={!newFeature.title.trim()}
          />
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default FeatureBacklog;
